import React, { useEffect, useRef, useState } from "react";
import { CustomAreaChart, Loader } from "@/components";
import { ApiResponse } from "@/types/Api";
import GenericApi from "@/api/genericApi";
import {
  CurrencyQueryKey,
  StatisticsQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { Currency } from "@/types/Currency";
import { useQuery } from "@tanstack/react-query";
import { FeeResponse, VolumeResponse } from "@/types/Fee";
import { AnimatePresence, motion } from "framer-motion";
import { isBitramp } from "@/utils/utils";

const GraphArea: React.FC = () => {
  const [activeButton, setActiveButton] = useState<string>("fees");
  const [selectedCurrency, setSelectedCurrency] = useState<string>("BTC");

  const handleButtonClick = (button: string) => {
    setActiveButton(button);
  };

  const [startDate, setStartDate] = useState<number>(7);
  const [openCurency, setOpenCurency] = useState<boolean>(false);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getOtherdayDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - startDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery<
    ApiResponse<Currency[]>
  >({
    queryFn: async () => GenericApi.get<Currency[]>(ApiRoutes.CURRENCY),
    queryKey: [CurrencyQueryKey],
  });

  // const handleCurrencyChange = (
  //   event: React.ChangeEvent<HTMLSelectElement>
  // ) => {
  //   setSelectedCurrency(event.target.value);
  // };

  const { data: feeReport, isLoading: isLoadingFeeReport } = useQuery<
    ApiResponse<FeeResponse[]>
  >({
    queryFn: async () => {
      const today = getTodayDate();
      const yesterday = getOtherdayDate();
      return GenericApi.get<FeeResponse[]>(
        `${ApiRoutes.STATISTICS}/fee/${
          startDate > 30 ? "monthly" : "daily"
        }?start_date=${yesterday}&end_date=${today}&currency=${selectedCurrency}`
      );
    },
    queryKey: [StatisticsQueryKey, selectedCurrency, startDate, activeButton],
  });

  const transformData = (data: FeeResponse[]) => {
    return data.map((item) => ({
      name: item.fee_date,
      uv: Number(item.fee),
    }));
  };

  const [chartDataFee, setChartDataFee] = useState<
    { name: string; uv: number }[]
  >([]);

  useEffect(() => {
    if (feeReport?.data) {
      setChartDataFee(transformData(feeReport.data));
    }
  }, [feeReport]);

  let totalFee = 0;

  if (feeReport?.data) {
    for (let i = 0; i < feeReport.data.length; i++) {
      totalFee += feeReport.data[i].fee;
    }
  }

  let cleanedFee = parseFloat(totalFee.toString()).toFixed(13);

  const { data: volumeReport, isLoading: isLoadingVolumeReport } = useQuery<
    ApiResponse<VolumeResponse[]>
  >({
    queryFn: async () => {
      const today = getTodayDate();
      const yesterday = getOtherdayDate();
      return GenericApi.get<VolumeResponse[]>(
        `${ApiRoutes.STATISTICS}/volume/${
          startDate > 30 ? "monthly" : "daily"
        }?start_date=${yesterday}&end_date=${today}&currency=${selectedCurrency}`
      );
    },
    queryKey: [selectedCurrency, startDate, activeButton],
  });

  const transformDataVolume = (data: VolumeResponse[]) => {
    return data.map((item) => ({
      name: item.volume_date,
      uv: Number(item.volume),
    }));
  };

  const [chartDataVolume, setChartDataVolume] = useState<
    { name: string; uv: number }[]
  >([]);

  useEffect(() => {
    if (volumeReport?.data) {
      setChartDataVolume(transformDataVolume(volumeReport.data));
    }
  }, [volumeReport]);

  let totalVolume = 0;

  if (volumeReport?.data) {
    for (let i = 0; i < volumeReport.data.length; i++) {
      totalVolume += volumeReport.data[i].volume;
    }
  }

  let cleanedVolume = parseFloat(totalVolume.toString()).toFixed(13);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenCurency(false);
      }
    }

    if (openCurency) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openCurency]);

  if (isLoadingFeeReport || isLoadingCurrencies || isLoadingVolumeReport) {
    return <Loader />;
  }

  return (
    <div className="w-full min-h-[33.6rem] flex flex-col">
      <div className="flex justify-center items-center gap-4">
        <button
          className={`font-[700] flex-1 py-3.5 ${
            isBitramp()
              ? activeButton === "volume"
                ? "Upload_button_bitramp"
                : "No_Outline_Button pshadow"
              : activeButton === "volume"
              ? "Primary_button"
              : "No_Outline_Button pshadow"
          }`}
          onClick={() => handleButtonClick("volume")}
        >
          Volume
        </button>
        <button
          className={`font-[700] flex-1 py-3.5 ${
            isBitramp()
              ? activeButton === "fees"
                ? "Upload_button_bitramp"
                : "No_Outline_Button pshadow"
              : activeButton === "fees"
              ? "Primary_button"
              : "No_Outline_Button pshadow"
          }`}
          onClick={() => handleButtonClick("fees")}
        >
          Fees
        </button>
      </div>
      <div className="Container mt-3 p-3 flex flex-col pshadow">
        <div className="flex items-center w-full justify-between px-5">
          <h1 className="Subtitle bold dark-blue">Volume</h1>
          <ul className="list-none flex justify-center items-center gap-5">
            <li>
              <button
                className={`Subtitle semi-bold ${
                  // startDate == 7 ? " dark-blue" : "dark-gray"
                  isBitramp()
                    ? startDate == 7
                      ? "cyan"
                      : "dark-blue"
                    : startDate == 7
                    ? "dark-blue"
                    : "dark-gray"
                }`}
                onClick={() => setStartDate(7)}
              >
                1W
              </button>
            </li>
            <li>
              <button
                className={`Subtitle semi-bold ${
                  // startDate == 30 ? " dark-blue" : "dark-gray"
                  isBitramp()
                    ? startDate == 30
                      ? "cyan"
                      : "dark-blue"
                    : startDate == 30
                    ? "dark-blue"
                    : "dark-gray"
                }`}
                onClick={() => setStartDate(30)}
              >
                1M
              </button>
            </li>
            <li>
              <button
                className={`Subtitle semi-bold ${
                  // startDate == 356 ? " dark-blue" : "dark-gray"
                  isBitramp()
                    ? startDate == 356
                      ? "cyan"
                      : "dark-blue"
                    : startDate == 356
                    ? "dark-blue"
                    : "dark-gray"
                }`}
                onClick={() => setStartDate(356)}
              >
                1Y
              </button>
            </li>
          </ul>
        </div>
        <hr
          className={`border-[0.3px] opacity-50 ${
            isBitramp() ? "border-[#464646]" : "border-[#EFEFEF]"
          } my-3 px-5`}
        />
        <div className="flex justify-between items-center px-5">
          <p className="Text dark-gray">Volume Balance</p>
          <p className="Text dark-gray">Currency</p>
        </div>
        <div className="flex justify-between items-center px-5">
          <p className="Subtitle dark-blue bold">
            {activeButton == "fees"
              ? cleanedFee.slice(0, 13)
              : cleanedVolume.slice(0, 13)}
          </p>
          {/* <select
            className="Subtitle dark-blue bold border-none focus:border-none focus:ring-0 bg-white"
            value={selectedCurrency}
            onChange={handleCurrencyChange}
          >
            {currencies?.data?.map((currency) => (
              <option key={currency.symbol} value={currency.symbol}>
                {currency.symbol}
              </option>
            ))}
          </select> */}

          <div
            className={`relative py-2 Container border-none focus:ring-0  cursor-pointer w-fit ps-4 pe-8 Input semi-bold dark-blue Subtitle ${
              isBitramp() ? "!bg-[#2B2B2B]" : ""
            }`}
          >
            <div
              onClick={() => setOpenCurency(!openCurency)}
              className="h-full flex justify-start items-center gap-2 cursor-pointer"
            >
              {selectedCurrency}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="10"
                viewBox="0 0 11 10"
                fill="none"
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <path
                  d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                  fill={isBitramp() ? "#fff" : "#737791"}
                />
              </svg>
            </div>

            {/* <AnimatePresence>
              {openCurency && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  // className="absolute top-full left-0 w-full Container pshadow mt-1 z-10"
                  className={`absolute top-full GlassContainerc left-0 w-full Container pshadow mt-1 z-10 ${
                    isBitramp()
                      ? "bg-[#2b2b2b] text-white border border-[rgba(255,255,255,0.1)]"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  {currencies?.data?.map((currency) => (
                    <div
                      key={currency.symbol}
                      onClick={() => {
                        setSelectedCurrency(currency?.symbol ?? "");
                        setOpenCurency(false);
                      }}
                      className={`${
                        isBitramp()
                          ? "px-4 py-2 hover:bg-[#262626] hover:rounded-[0.625rem] cursor-pointer flex justify-start align-center gap-2"
                          : "px-4 py-2 hover:bg-gray-100 hover:rounded-[0.625rem] cursor-pointer flex justify-start align-center gap-2"
                      }`}
                    >
                      {currency.symbol}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence> */}

            <AnimatePresence>
              {openCurency && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 GlassContainerc dark-blue pshadow z-10 h-[13rem] flex justify-center items-center"
                >
                  <motion.div
                    ref={dropdownRef}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className={`dark-blue w-[93%] z-10 h-[12rem] overflow-y-auto ${
                      isBitramp() ? "crypto-list_bitramp" : "crypto-list-gray"
                    }`}
                  >
                    {currencies?.data?.map((currency) => (
                      <div
                        key={currency.symbol}
                        onClick={() => {
                          setSelectedCurrency(currency?.symbol ?? "");
                          setOpenCurency(false);
                        }}
                        className="px-4 py-2 hover:bg-gray-400 cursor-pointer rounded-[0.625rem] flex justify-start align-center gap-2"
                      >
                        {currency.symbol}
                      </div>
                    ))}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <div className="my-4" />
        {activeButton === "volume" ? (
          <CustomAreaChart customData={chartDataVolume} />
        ) : (
          <CustomAreaChart customData={chartDataFee} />
        )}
      </div>
      {/*<img src='https://placehold.co/1000x500' alt="Placeholder" />*/}
    </div>
  );
};

export default GraphArea;
